import type { BackofficePrincipal, TokenProvider } from './types';

export interface SsoConfiguration {
  readonly apiBaseUrl: string;
  readonly applicationId: string;
  readonly iamApiBaseUrl: string;
  readonly iamPortalUrl: string;
}

interface LinkTokenResponse {
  readonly access_token: string;
  readonly application_id: string;
  readonly expires_in?: number;
  readonly refresh_token: string;
  readonly ttl: number;
}

interface RefreshTokenResponse {
  readonly access_token: string;
  readonly expires_in?: number;
  readonly refresh_token?: string;
}

interface AuthMeResponse {
  readonly data: BackofficePrincipal;
}

export interface SessionDependencies {
  readonly fetch?: typeof fetch;
  readonly now?: () => Date;
  readonly storage?: Storage;
}

export interface IamNavigation {
  readonly currentUrl: () => string;
  readonly redirect: (url: string) => void;
  readonly replaceHistory: (url: string) => void;
}

export interface IamTokenProviderDependencies extends SessionDependencies {
  readonly navigation?: IamNavigation;
}

export type SessionFailureKind =
  'configuration' | 'forbidden' | 'invalid_link' | 'unauthorized' | 'unavailable';

export class SessionError extends Error {
  public readonly kind: SessionFailureKind;
  public readonly status: number | null;

  public constructor(kind: SessionFailureKind, message: string, status: number | null = null) {
    super(message);
    this.name = 'SessionError';
    this.kind = kind;
    this.status = status;
  }
}

const accessTokenKey = 'affiliate.backoffice.sso.access-token';
const refreshTokenKey = 'affiliate.backoffice.sso.refresh-token';
const expiresAtKey = 'affiliate.backoffice.sso.expires-at';
const tokenExpiryLeewayMilliseconds = 30_000;

export function createIamTokenProvider(
  configuration: SsoConfiguration,
  dependencies: IamTokenProviderDependencies = {},
): TokenProvider {
  const storage = dependencies.storage ?? browserStorage();
  const navigation = dependencies.navigation ?? browserNavigation();
  const sessionDependencies = { ...dependencies, storage };
  let callbackExchange: Promise<void> | null = null;
  let redirectStarted = false;

  async function consumeIamCallback(): Promise<void> {
    const rawUrl = navigation.currentUrl();
    const linkToken = new URL(rawUrl).searchParams.get('pk');
    if (linkToken === null) return;

    // Remove every pk value before making a network request so the one-time credential does not
    // remain in browser history, screenshots, or copied URLs.
    navigation.replaceHistory(callbackUrlWithoutLinkToken(rawUrl));
    clearStoredSession(storage);
    await exchangeLinkToken(linkToken, configuration, sessionDependencies);
  }

  function returnToIam(): void {
    clearStoredSession(storage);
    if (redirectStarted) return;
    redirectStarted = true;
    navigation.redirect(configuration.iamPortalUrl);
  }

  return Object.freeze({
    async getIdToken(): Promise<string | null> {
      callbackExchange ??= consumeIamCallback();
      await callbackExchange;
      validateSsoConfiguration(configuration);

      const accessToken = await getValidAccessToken(configuration, sessionDependencies);
      if (accessToken !== null) return accessToken;

      returnToIam();
      return null;
    },
    async signOut(): Promise<void> {
      returnToIam();
    },
  });
}

export function validateSsoConfiguration(configuration: SsoConfiguration): void {
  if (!isUuid(configuration.applicationId)) {
    throw new SessionError(
      'configuration',
      'The IAM application ID is not configured for this environment.',
    );
  }
  for (const [label, value] of [
    ['Backoffice API', configuration.apiBaseUrl],
    ['IAM API', configuration.iamApiBaseUrl],
    ['IAM portal', configuration.iamPortalUrl],
  ] as const) {
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      throw new SessionError('configuration', `${label} URL is not configured.`);
    }
    if (!['http:', 'https:'].includes(url.protocol)) {
      throw new SessionError('configuration', `${label} URL is not an HTTP URL.`);
    }
  }
}

export function callbackUrlWithoutLinkToken(rawUrl: string): string {
  const url = new URL(rawUrl);
  url.searchParams.delete('pk');
  return `${url.pathname}${url.search}${url.hash}`;
}

export function clearStoredSession(storage: Storage = browserStorage()): void {
  storage.removeItem(accessTokenKey);
  storage.removeItem(refreshTokenKey);
  storage.removeItem(expiresAtKey);
}

export function getStoredAccessToken(storage: Storage = browserStorage()): string | null {
  return storage.getItem(accessTokenKey);
}

export function hasStoredSession(storage: Storage = browserStorage()): boolean {
  return getStoredAccessToken(storage) !== null && storage.getItem(refreshTokenKey) !== null;
}

export async function exchangeLinkToken(
  linkToken: string,
  configuration: SsoConfiguration,
  dependencies: SessionDependencies = {},
): Promise<void> {
  validateSsoConfiguration(configuration);
  if (!isLinkToken(linkToken)) {
    throw new SessionError('invalid_link', 'The IAM sign-in link is invalid or expired.');
  }

  const fetchImplementation = dependencies.fetch ?? fetch;
  const response = await request(
    fetchImplementation,
    `${baseUrl(configuration.iamApiBaseUrl)}/auth/link-token?hash_key=${encodeURIComponent(linkToken)}`,
    {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      headers: { accept: 'application/json' },
      referrerPolicy: 'no-referrer',
    },
    'invalid_link',
    'IAM could not redeem this sign-in link.',
  );
  if (!response.ok) {
    throw new SessionError(
      response.status >= 500 ? 'unavailable' : 'invalid_link',
      response.status >= 500
        ? 'IAM is temporarily unavailable.'
        : 'The IAM sign-in link is invalid or expired.',
      response.status,
    );
  }

  const payload = await jsonObject(response, 'invalid_link');
  if (!isLinkTokenResponse(payload)) {
    throw new SessionError('invalid_link', 'IAM returned an invalid sign-in response.');
  }
  if (payload.application_id.toLowerCase() !== configuration.applicationId.toLowerCase()) {
    throw new SessionError(
      'forbidden',
      'This IAM sign-in link was issued for a different application.',
    );
  }

  const now = dependencies.now?.() ?? new Date();
  if (payload.ttl * 1_000 <= now.getTime()) {
    throw new SessionError('invalid_link', 'The IAM sign-in link is invalid or expired.');
  }

  persistSession(payload, dependencies.storage ?? browserStorage(), now, {
    replaceRefreshToken: true,
  });
}

export async function refreshSession(
  configuration: SsoConfiguration,
  dependencies: SessionDependencies = {},
): Promise<string | null> {
  validateSsoConfiguration(configuration);
  const storage = dependencies.storage ?? browserStorage();
  const accessToken = storage.getItem(accessTokenKey);
  const refreshToken = storage.getItem(refreshTokenKey);
  if (accessToken === null || refreshToken === null) return null;

  const response = await request(
    dependencies.fetch ?? fetch,
    `${baseUrl(configuration.iamApiBaseUrl)}/auth/refresh-token`,
    {
      method: 'POST',
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${accessToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
      referrerPolicy: 'no-referrer',
    },
    'unavailable',
    'IAM is temporarily unavailable.',
  );
  if (!response.ok) {
    if (response.status >= 500 || response.status === 408 || response.status === 429) {
      throw new SessionError('unavailable', 'IAM is temporarily unavailable.', response.status);
    }
    return null;
  }

  const payload = await jsonObject(response, 'unavailable');
  if (!isRefreshTokenResponse(payload)) {
    throw new SessionError('unavailable', 'IAM returned an invalid refresh response.');
  }
  persistSession(payload, storage, dependencies.now?.() ?? new Date());
  return payload.access_token;
}

export async function getValidAccessToken(
  configuration: SsoConfiguration,
  dependencies: SessionDependencies = {},
): Promise<string | null> {
  const storage = dependencies.storage ?? browserStorage();
  const accessToken = storage.getItem(accessTokenKey);
  if (accessToken === null) return null;

  const expiresAt = storage.getItem(expiresAtKey);
  if (expiresAt === null) return accessToken;
  const expiryMilliseconds = Date.parse(expiresAt);
  const nowMilliseconds = (dependencies.now?.() ?? new Date()).getTime();
  if (
    Number.isNaN(expiryMilliseconds) ||
    expiryMilliseconds > nowMilliseconds + tokenExpiryLeewayMilliseconds
  ) {
    return accessToken;
  }
  return refreshSession(configuration, dependencies);
}

export async function fetchBackofficePrincipal(
  configuration: SsoConfiguration,
  dependencies: SessionDependencies = {},
): Promise<BackofficePrincipal> {
  validateSsoConfiguration(configuration);
  const accessToken = await getValidAccessToken(configuration, dependencies);
  if (accessToken === null) {
    throw new SessionError('unauthorized', 'No active IAM session is available.', 401);
  }

  const response = await request(
    dependencies.fetch ?? fetch,
    `${baseUrl(configuration.apiBaseUrl)}/api/v1/auth/me`,
    {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      referrerPolicy: 'no-referrer',
    },
    'unavailable',
    'The Affiliate Backoffice API is temporarily unavailable.',
  );

  if (!response.ok) {
    const message = await responseMessage(response);
    if (response.status === 401) {
      throw new SessionError('unauthorized', message ?? 'The IAM session has expired.', 401);
    }
    if (response.status === 403) {
      throw new SessionError(
        'forbidden',
        message ?? 'This IAM account does not have Affiliate Platform access.',
        403,
      );
    }
    throw new SessionError(
      'unavailable',
      message ?? 'The Affiliate Backoffice API is temporarily unavailable.',
      response.status,
    );
  }

  const payload = await jsonObject(response, 'unavailable');
  if (!isAuthMeResponse(payload)) {
    throw new SessionError(
      'unavailable',
      'The Affiliate Backoffice API returned an invalid session.',
    );
  }
  return payload.data;
}

function persistSession(
  payload: RefreshTokenResponse,
  storage: Storage,
  now: Date,
  options: { readonly replaceRefreshToken?: boolean } = {},
): void {
  storage.setItem(accessTokenKey, payload.access_token);
  if (payload.refresh_token !== undefined) {
    storage.setItem(refreshTokenKey, payload.refresh_token);
  } else if (options.replaceRefreshToken === true) {
    storage.removeItem(refreshTokenKey);
  }

  const expiresAt =
    expiryFromDuration(payload.expires_in, now) ??
    expiryFromJwt(payload.access_token) ??
    new Date(now.getTime() + 60 * 60 * 1_000).toISOString();
  storage.setItem(expiresAtKey, expiresAt);
}

function expiryFromDuration(expiresIn: number | undefined, now: Date): string | null {
  if (expiresIn === undefined || !Number.isFinite(expiresIn) || expiresIn <= 0) return null;
  return new Date(now.getTime() + expiresIn * 1_000).toISOString();
}

function expiryFromJwt(accessToken: string): string | null {
  const payload = accessToken.split('.')[1];
  if (payload === undefined) return null;
  try {
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const decoded = JSON.parse(globalThis.atob(padded)) as unknown;
    if (!isRecord(decoded) || typeof decoded['exp'] !== 'number') return null;
    return new Date(decoded['exp'] * 1_000).toISOString();
  } catch {
    return null;
  }
}

async function request(
  fetchImplementation: typeof fetch,
  url: string,
  init: RequestInit,
  failureKind: SessionFailureKind,
  failureMessage: string,
): Promise<Response> {
  try {
    return await fetchImplementation(url, init);
  } catch {
    throw new SessionError(failureKind, failureMessage);
  }
}

async function jsonObject(
  response: Response,
  failureKind: SessionFailureKind,
): Promise<Record<string, unknown>> {
  try {
    const payload: unknown = await response.json();
    if (isRecord(payload)) return payload;
  } catch {
    // Fall through to the stable, token-free error below.
  }
  throw new SessionError(failureKind, 'The authentication service returned an invalid response.');
}

async function responseMessage(response: Response): Promise<string | null> {
  try {
    const payload: unknown = await response.clone().json();
    if (!isRecord(payload)) return null;
    const error = payload['error'];
    if (isRecord(error) && typeof error['message'] === 'string') return error['message'];
    return typeof payload['detail'] === 'string' ? payload['detail'] : null;
  } catch {
    return null;
  }
}

function isLinkTokenResponse(
  value: Record<string, unknown>,
): value is Record<string, unknown> & LinkTokenResponse {
  return (
    typeof value['access_token'] === 'string' &&
    value['access_token'].length > 0 &&
    typeof value['refresh_token'] === 'string' &&
    value['refresh_token'].length > 0 &&
    typeof value['application_id'] === 'string' &&
    isUuid(value['application_id']) &&
    typeof value['ttl'] === 'number' &&
    Number.isFinite(value['ttl']) &&
    (value['expires_in'] === undefined || typeof value['expires_in'] === 'number')
  );
}

function isRefreshTokenResponse(
  value: Record<string, unknown>,
): value is Record<string, unknown> & RefreshTokenResponse {
  return (
    typeof value['access_token'] === 'string' &&
    value['access_token'].length > 0 &&
    (value['refresh_token'] === undefined || typeof value['refresh_token'] === 'string') &&
    (value['expires_in'] === undefined || typeof value['expires_in'] === 'number')
  );
}

function isAuthMeResponse(
  value: Record<string, unknown>,
): value is Record<string, unknown> & AuthMeResponse {
  const principal = value['data'];
  if (!isRecord(principal)) return false;
  const actor = principal['actor'];
  const scope = principal['scope'];
  return (
    isRecord(actor) &&
    typeof actor['id'] === 'string' &&
    typeof actor['displayName'] === 'string' &&
    typeof principal['username'] === 'string' &&
    (typeof principal['email'] === 'string' || principal['email'] === null) &&
    ['SUPER_ADMIN', 'AGENT_SUPER_USER', 'AGENT_ADMIN_USER'].includes(String(principal['role'])) &&
    principal['status'] === 'ACTIVE' &&
    isRecord(scope) &&
    isNullableString(scope['partnerId']) &&
    isNullableString(scope['agentId']) &&
    isNullableString(scope['permissionProfileId']) &&
    Array.isArray(principal['permissions']) &&
    principal['permissions'].every((permission) => typeof permission === 'string')
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return typeof value === 'string' || value === null;
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value);
}

function isLinkToken(value: string): boolean {
  return value.length >= 32 && value.length <= 512 && /^[A-Za-z0-9_-]+$/u.test(value);
}

function baseUrl(value: string): string {
  return value.replace(/\/+$/u, '');
}

function browserStorage(): Storage {
  if (typeof window === 'undefined') {
    throw new SessionError('configuration', 'Browser session storage is unavailable.');
  }
  return window.localStorage;
}

function browserNavigation(): IamNavigation {
  if (typeof window === 'undefined') {
    throw new SessionError('configuration', 'Browser navigation is unavailable.');
  }
  return {
    currentUrl: () => window.location.href,
    redirect: (url) => window.location.replace(url),
    replaceHistory: (url) => window.history.replaceState({}, '', url),
  };
}
